import { DialogComponent } from '@theme/dialog';

/**
 * <plw-story-modal> — opens one story's fullscreen video dialog in place.
 *
 * Hosts: each article row on the Stories listing (sections/plw-stories.liquid)
 * and each homepage hero with an "Interview story" picked
 * (sections/plw-hero-split.liquid, sections/plw-hero-bottom.liquid — the hero
 * root itself becomes the <plw-story-modal>).
 *
 * Extends Horizon's {@link DialogComponent}, inheriting the native
 * `<dialog showModal>` machinery: top-layer + focus trap, scroll-lock,
 * outside-click + ESC close, and the `dialog:open` / `dialog:close` events.
 *
 * Progressive enhancement over the host's trigger anchors, all href'd to the
 * article's canonical URL: `[data-story-trigger]` anchors (the listing's
 * poster tile and mobile "Watch video" button) plus — when the host carries
 * `data-story-url` — every descendant anchor whose pathname equals that URL
 * (the heroes' "Watch interview" CTAs, including the generic Horizon button
 * block, which can't carry a data attribute). Plain left-clicks open the
 * dialog instead of navigating; modified clicks (new tab etc.) and JS-off keep
 * native link behavior. The video plays once the dialog is open (the click
 * gesture authorizes audible playback; if the browser still refuses, the
 * native controls are right there) and pauses on close, keeping `currentTime`
 * so reopening resumes.
 *
 * A `[data-story-next]` link inside the dialog (the listing's "Watch next
 * interview" pill, href'd to the next story) closes this dialog and opens the
 * <plw-story-modal data-story-url> on the page that matches its path — the
 * next row — instead of navigating; if no such host exists (next story on
 * another page) the link navigates as normal.
 *
 * @extends DialogComponent
 */
class PlwStoryModal extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();

    for (const trigger of this.#triggers) {
      trigger.addEventListener('click', this.#handleTriggerClick);
      trigger.setAttribute('aria-haspopup', 'dialog');
    }

    this.#nextLink?.addEventListener('click', this.#handleNextClick);

    this.addEventListener('dialog:open', this.#playVideo);
    this.addEventListener('dialog:close', this.#pauseVideo);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    for (const trigger of this.#triggers) {
      trigger.removeEventListener('click', this.#handleTriggerClick);
    }
    this.#nextLink?.removeEventListener('click', this.#handleNextClick);

    this.removeEventListener('dialog:open', this.#playVideo);
    this.removeEventListener('dialog:close', this.#pauseVideo);
    this.#pauseVideo();
  }

  /**
   * The anchors that open the dialog: explicit `[data-story-trigger]`s plus,
   * given `data-story-url`, any anchor pointing at that path (hash/query
   * ignored, so `/blogs/stories#play` never matches `/blogs/stories/kati`).
   *
   * @returns {HTMLAnchorElement[]}
   */
  get #triggers() {
    const triggers = new Set(
      /** @type {NodeListOf<HTMLAnchorElement>} */ (this.querySelectorAll('[data-story-trigger]'))
    );

    const storyPath = this.dataset.storyUrl;
    if (storyPath) {
      for (const anchor of this.querySelectorAll('a[href]')) {
        try {
          const url = new URL(anchor.getAttribute('href') ?? '', window.location.origin);
          if (url.origin === window.location.origin && url.pathname === storyPath) {
            triggers.add(/** @type {HTMLAnchorElement} */ (anchor));
          }
        } catch {
          // Unparseable href — not a trigger.
        }
      }
    }

    return [...triggers];
  }

  /**
   * @param {MouseEvent} event
   */
  #handleTriggerClick = (event) => {
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    this.showDialog();
  };

  /**
   * "Watch next interview": hand off to the host whose story the link points
   * at, when it's on the page.
   *
   * @param {MouseEvent} event
   */
  #handleNextClick = async (event) => {
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = /** @type {HTMLAnchorElement} */ (event.currentTarget);
    let path;
    try {
      const url = new URL(link.getAttribute('href') ?? '', window.location.origin);
      if (url.origin !== window.location.origin) return;
      path = url.pathname;
    } catch {
      return;
    }

    const next = /** @type {PlwStoryModal | null} */ (
      document.querySelector(`plw-story-modal[data-story-url="${CSS.escape(path)}"]`)
    );
    if (!next) return;

    event.preventDefault();
    await this.closeDialog();
    if (next === this) {
      if (this.#video) this.#video.currentTime = 0;
    }
    next.showDialog();
  };

  get #nextLink() {
    return /** @type {HTMLAnchorElement | null} */ (this.refs.dialog?.querySelector('[data-story-next]'));
  }

  #playVideo = () => {
    this.#video?.play().catch(() => {});
  };

  #pauseVideo = () => {
    this.#video?.pause();
  };

  get #video() {
    return /** @type {HTMLVideoElement | null} */ (this.refs.dialog?.querySelector('video'));
  }
}

if (!customElements.get('plw-story-modal')) {
  customElements.define('plw-story-modal', PlwStoryModal);
}
